import React, { useEffect, useRef, useState } from "react";
import ml5 from "ml5";
import p5 from "p5";
import { useNavigate } from "react-router-dom";

const words = ['apple', 'banana', 'car', 'tree', 'cat', 'fish', 'star', 'flower', 'cup'];

function QuickDrawPage() {
  const sketchRef = useRef(null);
  const guessIntervalRef = useRef(null);
  const classifierRef = useRef(null);
  const targetWordRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  const [errorMsg, setErrorMsg] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [guess, setGuess] = useState("");
  const [targetWord, setTargetWord] = useState("");
  const [resultText, setResultText] = useState("");

  useEffect(() => {
    let myp5;
    function sketch(p) {
      p.setup = () => {
        p.createCanvas(280, 280).parent(sketchRef.current);
        p.background(255);

        classifierRef.current = ml5.imageClassifier('DoodleNet', () => {});

        // new target word
        const newWord = p.random(words);
        targetWordRef.current = newWord;
        setTargetWord(newWord);

        //guessing every 2 seconds
        guessIntervalRef.current = setInterval(() => classifyDrawing(p), 2000);
      };

      p.draw = () => {
        if (p.mouseIsPressed) {
          p.stroke(0);
          p.strokeWeight(10);
          p.line(p.pmouseX, p.pmouseY, p.mouseX, p.mouseY);
        }
      };
    }

    myp5 = new p5(sketch);

    function preprocessCanvas(p) {
      const smallCanvas = p.createGraphics(28, 28);
      smallCanvas.background(255);
      let img = null;
      try {
        img = p.get();
      } catch (e) {
        return smallCanvas.canvas;
      }
      if (img && typeof img.width === "number" && typeof img.height === "number" && img.width > 0 && img.height > 0) {
        smallCanvas.image(img, 0, 0, 28, 28);
      } else {
        return smallCanvas.canvas;
      }
      smallCanvas.loadPixels();
      for (let i = 0; i < smallCanvas.pixels.length; i += 4) {
        let avg = (smallCanvas.pixels[i] + smallCanvas.pixels[i+1] + smallCanvas.pixels[i+2]) / 3;
        smallCanvas.pixels[i] = avg;
        smallCanvas.pixels[i+1] = avg;
        smallCanvas.pixels[i+2] = avg;
      }
      smallCanvas.updatePixels();
      return smallCanvas.canvas;
    }

    function classifyDrawing(p) {
      if (!classifierRef.current) return;
      const processedCanvas = preprocessCanvas(p);
      classifierRef.current.classify(processedCanvas, (error, results) => {
        if (error) {
          try {
            const preds = JSON.parse(error);
            if (Array.isArray(preds)) {
              setPredictions(preds);
              setErrorMsg("");
              setGuess(preds[0]?.label || "");
              return;
            }
          } catch (e) {
            setErrorMsg(typeof error === "string" ? error : JSON.stringify(error, null, 2));
            setPredictions([]);
            setGuess("");
            return;
          }
        }
        setErrorMsg("");
        setPredictions(results);
        setGuess(results[0]?.label || "");
        // Show result
        const bestGuess = results[0]?.label?.toLowerCase() || "";
        if (bestGuess && bestGuess === targetWordRef.current?.toLowerCase()) {
          setResultText(`✅ Correct! Robot guessed: ${bestGuess}`);
          clearInterval(guessIntervalRef.current);
          guessIntervalRef.current = null;
        } else {
          setResultText(`❌ Robot guessed: ${bestGuess}. Keep trying!`);
        }
      });
    }

    return () => {
      if (guessIntervalRef.current) clearInterval(guessIntervalRef.current);
      if (myp5) myp5.remove();
    };
  }, []);

  function handleClear() {
    setResultText('');
    setPredictions([]);
    setGuess('');
    setErrorMsg('');
    const nextWord = words[Math.floor(Math.random() * words.length)];
    setTargetWord(nextWord);
    if (targetWordRef) targetWordRef.current = nextWord;
    if (sketchRef.current && sketchRef.current.children[0]) {
      const canvas = sketchRef.current.children[0];
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, 280, 280);
    }
    if (!guessIntervalRef.current) {
      guessIntervalRef.current = setInterval(() => {
        if (window.myp5 && window.myp5._setupDone) {
          classifyDrawing(window.myp5);
        }
      }, 2000);
    }
  }

  if (typeof window !== "undefined") {
    const origWarn = console.warn;
    console.warn = function (...args) {
      if (
        typeof args[0] === "string" &&
        args[0].includes("The shape of the input tensor") &&
        args[0].includes("does not match the expectation of layer conv2d_1")
      ) {
        return;
      }
      origWarn.apply(console, args);
    };
  }

  let predictionsToShow = predictions;
  if (!predictions.length && errorMsg) {
    try {
      const preds = JSON.parse(errorMsg);
      if (Array.isArray(preds)) {
        predictionsToShow = preds;
      }
    } catch (e) {}
  }

  return (
    <div style={{ minHeight: "100vh", background: "#fafafa", textAlign: "center", paddingTop: 40 }}>
      <h2>Quick Draw!</h2>
      <p style={{ fontSize: "1.5rem" }}>
        Draw: <b>{targetWord}</b>
      </p>
      <div
        ref={sketchRef}
        id="sketch-holder"
        style={{
          display: "inline-block",
          border: "2px solid #444",
          marginTop: 20,
        }}
      />
      <div style={{ marginTop: 20 }}>
        <button onClick={handleClear}>Clear & New Word</button>
        <button onClick={() => navigate("/")} style={{ marginLeft: 10 }}>
          Back
        </button>
      </div>
      <div style={{ marginTop: 30, fontSize: "1.2rem" }}>
        <div>{resultText}</div>
        {/* Show predictions*/}
        {predictionsToShow.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div>
              <b>Computer is guessing:</b> <span>{predictionsToShow[0]?.label || "..."}</span>
            </div>
            <ul style={{ listStyle: "none", padding: 0 }}>
              {predictionsToShow.slice(0, 3).map((pred, idx) => (
                <li key={idx}>
                  {idx + 1}. {pred.label} ({(pred.confidence * 100).toFixed(1)}%)
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Show errorrs */}
        {errorMsg && predictionsToShow.length === 0 && (
          <div style={{ color: "red", marginTop: 20, whiteSpace: "pre-wrap" }}>
            <b>Error:</b> {errorMsg}
          </div>
        )}
      </div>
    </div>
  );
}

export default QuickDrawPage;